import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "@/contexts/LanguageContext";
import { 
  FolderKanban, 
  Plus, 
  Trash2, 
  Edit,
  Search,
  Users
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/toast";
import { getGroups, createGroup, deleteGroup } from "@/lib/marketing-api";

const getPlatforms = (isRTL) => [
  { value: "telegram", label: isRTL ? "تلجرام" : "Telegram" },
  { value: "whatsapp", label: isRTL ? "واتساب" : "WhatsApp" },
  { value: "facebook", label: isRTL ? "فيسبوك" : "Facebook" },
  { value: "instagram", label: isRTL ? "انستجرام" : "Instagram" },
  { value: "tiktok", label: isRTL ? "تيك توك" : "TikTok" },
  { value: "youtube", label: isRTL ? "يوتيوب" : "YouTube" },
  { value: "linkedin", label: isRTL ? "لينكد إن" : "LinkedIn" },
  { value: "google_maps", label: isRTL ? "جوجل ماب" : "Google Maps" },
];

export default function Groups() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const platforms = getPlatforms(isRTL);
  const [formData, setFormData] = useState({
    name: "",
    platform: "telegram",
    group_id: "",
    member_count: 0,
    description: ""
  });

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    try {
      const response = await getGroups();
      setGroups(response.data?.data || response.data || []);
    } catch (error) {
      toast.error(isRTL ? "حدث خطأ في تحميل المجموعات" : "Error loading groups");
      setGroups([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await createGroup({
        ...formData,
        member_count: parseInt(formData.member_count) || 0
      });
      toast.success(t('success.created'));
      setIsDialogOpen(false);
      setFormData({
        name: "",
        platform: "telegram",
        group_id: "",
        member_count: 0,
        description: ""
      });
      loadGroups();
    } catch (error) {
      toast.error(t('errors.serverError'));
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('marketingFull.confirmDeleteGroup'))) return;
    try {
      await deleteGroup(id);
      toast.success(t('success.deleted'));
      loadGroups();
    } catch (error) {
      toast.error(t('errors.serverError'));
    }
  };

  const filteredGroups = Array.isArray(groups) ? groups.filter(group => {
    const matchesSearch = (group.name || '').toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPlatform = platformFilter === "all" || group.platform === platformFilter;
    return matchesSearch && matchesPlatform;
  }) : [];

  const getPlatformLabel = (value) => {
    const platform = platforms.find(p => p.value === value);
    return platform ? platform.label : value;
  };

  const totalMembers = Array.isArray(groups) ? groups.reduce((sum, g) => sum + (g.member_count || 0), 0) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="groups-page">
      {/* Header */}
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h1 className="text-3xl font-black text-foreground">{t('marketingFull.groupsTitle')}</h1>
          <p className="text-muted-foreground mt-1">{t('marketingFull.groupsSubtitle')}</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold px-6 py-2 shadow-lg shadow-emerald-500/30 text-base" size="lg" data-testid="add-group-btn">
              <Plus className="w-5 h-5 me-2" />
              + {t('marketingFull.addGroup')}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[500px]" dir={isRTL ? "rtl" : "ltr"}>
            <DialogHeader>
              <DialogTitle>{t('marketingFull.createGroup')}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4 mt-4">
              <div className="form-group">
                <Label htmlFor="name">{t('marketingFull.groupName')}</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder={t('marketingFull.groupName')}
                  required
                  data-testid="group-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="form-group">
                  <Label htmlFor="platform">{t('marketingFull.platform')}</Label>
                  <Select 
                    value={formData.platform} 
                    onValueChange={(value) => setFormData({ ...formData, platform: value })}
                  >
                    <SelectTrigger data-testid="group-platform-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {platforms.map(p => (
                        <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="form-group">
                  <Label htmlFor="member_count">{t('marketingFull.memberCount')}</Label>
                  <Input
                    id="member_count"
                    type="number"
                    value={formData.member_count}
                    onChange={(e) => setFormData({ ...formData, member_count: e.target.value })}
                    placeholder="0"
                    data-testid="group-members-input"
                  />
                </div>
              </div>
              <div className="form-group">
                <Label htmlFor="group_id">{t('marketingFull.channelId')}</Label>
                <Input
                  id="group_id"
                  value={formData.group_id}
                  onChange={(e) => setFormData({ ...formData, group_id: e.target.value })}
                  placeholder={t('marketingFull.groupIdPlaceholder')}
                  data-testid="group-id-input"
                />
              </div>
              <div className="form-group">
                <Label htmlFor="description">{t('marketingFull.groupDescription')}</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder={t('marketingFull.groupDescription')}
                  data-testid="group-description-input"
                />
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  {t('common.cancel')}
                </Button>
                <Button type="submit" data-testid="submit-group-btn">
                  {t('common.add')}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="stat-card">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
              <FolderKanban className="w-6 h-6 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('marketingFull.totalGroups')}</p>
              <p className="text-2xl font-black font-numbers">{groups.length}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-green-500/10 flex items-center justify-center">
              <Users className="w-6 h-6 text-green-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('marketingFull.totalMembers')}</p>
              <p className="text-2xl font-black font-numbers">{totalMembers.toLocaleString(isRTL ? "ar-SA" : "en-US")}</p>
            </div>
          </CardContent>
        </Card>
        <Card className="stat-card">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <FolderKanban className="w-6 h-6 text-blue-400" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('marketingFull.activeGroups')}</p>
              <p className="text-2xl font-black font-numbers">
                {groups.filter(g => g.is_active).length}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder={t('marketingFull.searchGroups')}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pe-10"
                data-testid="search-groups-input"
              />
            </div>
            <Select value={platformFilter} onValueChange={setPlatformFilter}>
              <SelectTrigger className="w-full md:w-[180px]" data-testid="platform-filter">
                <SelectValue placeholder={t('marketingFull.platform')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('marketingFull.allPlatforms')}</SelectItem>
                {platforms.map(p => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Groups Grid */}
      {filteredGroups.length === 0 ? (
        <Card>
          <CardContent className="py-12">
            <div className="empty-state">
              <FolderKanban className="empty-state-icon" />
              <h3 className="empty-state-title">{t('marketingFull.noGroups')}</h3>
              <p className="empty-state-description">{t('marketingFull.createFirstGroup')}</p>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredGroups.map((group) => (
            <Card key={group.id} className="card-hover" data-testid={`group-card-${group.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <CardTitle className="text-lg">{group.name}</CardTitle>
                  <span className={`platform-badge platform-${group.platform}`}>
                    {getPlatformLabel(group.platform)}
                  </span>
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                  {group.description || "لا يوجد وصف"}
                </p>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Users className="w-4 h-4" />
                  <span>عدد الأعضاء: </span>
                  <span className="font-numbers font-bold text-foreground">
                    {group.member_count.toLocaleString("ar-SA")}
                  </span>
                </div>
                {group.group_id && (
                  <div className="mt-2 text-sm text-muted-foreground font-numbers">
                    {group.group_id}
                  </div>
                )}
                <div className="flex gap-2 mt-4 pt-4 border-t border-border">
                  <Button size="sm" variant="outline" className="flex-1">
                    <Users className="w-4 h-4 me-1" />
                    استخراج الأعضاء
                  </Button>
                  <Button size="sm" variant="ghost">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button 
                    size="sm" 
                    variant="ghost" 
                    className="text-destructive"
                    onClick={() => handleDelete(group.id)}
                    data-testid={`delete-group-${group.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
